import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { GraduationCap, Plus, Pencil, Trash2, Search, Clock, CheckCircle, Building2, Globe, Infinity, Video, FileText, HelpCircle, Link2, Settings, ChevronRight, X, HardHat, Wrench, MapPin, Shield, BookOpen, Users } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODALIDADES = [
  { value: 'e-learning', label: 'E-Learning' },
  { value: 'presencial', label: 'Presencial' },
  { value: 'mixta', label: 'Mixta' }
];

const CATEGORIAS = [
  { value: 'legal', label: 'Legales' },
  { value: 'especifica_cargo', label: 'Especificas del Cargo' },
  { value: 'transversal_obligatoria', label: 'Transversales Obligatorias' },
  { value: 'transversal_cargo', label: 'Transversales Segun Cargo' },
];

export const Capacitaciones = () => {
  const { isAdmin } = useAuth();
  const [capacitaciones, setCapacitaciones] = useState([]);
  const [areas, setAreas] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  const [editingCapacitacion, setEditingCapacitacion] = useState(null);
  const [selectedCapForConfig, setSelectedCapForConfig] = useState(null);
  const [preguntas, setPreguntas] = useState([]);
  const [prerequisitos, setPrerequisitos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [archivos, setArchivos] = useState([]);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    vigencia_meses: 12,
    vigencia_indefinida: false,
    obligatoria: true,
    es_transversal: false,
    calendarizable: false,
    area_id: '',
    actividad_id: '',
    video_url: '',
    pdf_url: '',
    horas: '',
    modalidad: 'e-learning',
    porcentaje_aprobacion: 70,
    imagen_url: '',
    tiempo_evaluacion_minutos: 30,
    tiempo_ilimitado: false,
    intentos_maximos: 3,
    intentos_ilimitados: false
  });
  const [preguntaForm, setPreguntaForm] = useState({
    pregunta: '',
    alternativa_a: '',
    alternativa_b: '',
    alternativa_c: '',
    alternativa_d: '',
    respuesta_correcta: 'A',
    orden: 1
  });
  const [editingPregunta, setEditingPregunta] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, itemId: null, tipo: '', deps: [], loadingDeps: false });
  const [cargosReales, setCargosReales] = useState([]);
  const [asignCargos, setAsignCargos] = useState([]);
  const [asignAreas, setAsignAreas] = useState([]);
  const [cargoSearch, setCargoSearch] = useState('');

  useEffect(function() {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      var responses = await Promise.all([
        axios.get(API + '/capacitaciones'),
        axios.get(API + '/areas'),
        axios.get(API + '/actividades'),
        axios.get(API + '/cargos'),
      ]);
      setCapacitaciones(responses[0].data);
      setAreas(responses[1].data);
      setCargos(responses[2].data);  // Ahora son actividades
      setCargosReales(responses[3].data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }

  function getAreaName(areaId) {
    if (!areaId) return 'Transversal';
    for (var i = 0; i < areas.length; i++) {
      if (areas[i].id === areaId) return areas[i].nombre;
    }
    return 'Sin área';
  }

  function getCargoName(cargoId) {
    if (!cargoId) return '';
    for (var i = 0; i < cargos.length; i++) {
      if (cargos[i].id === cargoId) return cargos[i].nombre;
    }
    return '';
  }

  // Alias para actividades (cargos ahora contiene actividades)
  function getActividadName(actividadId) {
    return getCargoName(actividadId);
  }

  function getFilteredCapacitaciones() {
    var result = [];
    var searchLower = search.toLowerCase();
    for (var i = 0; i < capacitaciones.length; i++) {
      var cap = capacitaciones[i];
      if (cap.nombre.toLowerCase().indexOf(searchLower) >= 0 ||
          getAreaName(cap.area_id).toLowerCase().indexOf(searchLower) >= 0) {
        result.push(cap);
      }
    }
    return result;
  }

  function openCreateDialog() {
    setEditingCapacitacion(null);
    setVideos([]);
    setArchivos([]);
    setFormData({ 
      nombre: '', descripcion: '', vigencia_meses: 12, vigencia_indefinida: false,
      obligatoria: true, es_transversal: false, calendarizable: false, area_id: '', actividad_id: '',
      video_url: '', pdf_url: '', horas: '', modalidad: 'e-learning', porcentaje_aprobacion: 70,
      imagen_url: '', tiempo_evaluacion_minutos: 30, tiempo_ilimitado: false,
      intentos_maximos: 3, intentos_ilimitados: false, categoria: ''
    });
    setIsDialogOpen(true);
  }

  async function openEditDialog(capacitacion) {
    setEditingCapacitacion(capacitacion);
    var esIndefinida = capacitacion.vigencia_meses === null;
    var tiempoIlimitado = !capacitacion.tiempo_evaluacion_minutos;
    var intentosIlimitados = !capacitacion.intentos_maximos || capacitacion.intentos_maximos === 999;
    
    setFormData({
      nombre: capacitacion.nombre,
      descripcion: capacitacion.descripcion || '',
      vigencia_meses: capacitacion.vigencia_meses || 12,
      vigencia_indefinida: esIndefinida,
      obligatoria: capacitacion.obligatoria,
      es_transversal: capacitacion.es_transversal || false,
      calendarizable: capacitacion.calendarizable || false,
      area_id: capacitacion.area_id || '',
      actividad_id: capacitacion.actividad_id || '',
      video_url: capacitacion.video_url || '',
      pdf_url: capacitacion.pdf_url || '',
      horas: capacitacion.horas || '',
      modalidad: capacitacion.modalidad || 'e-learning',
      porcentaje_aprobacion: capacitacion.porcentaje_aprobacion || 70,
      imagen_url: capacitacion.imagen_url || '',
      tiempo_evaluacion_minutos: capacitacion.tiempo_evaluacion_minutos || 30,
      tiempo_ilimitado: tiempoIlimitado,
      intentos_maximos: capacitacion.intentos_maximos || 3,
      intentos_ilimitados: intentosIlimitados,
      categoria: capacitacion.categoria || ''
    });
    
    // Cargar videos y archivos
    try {
      var videosRes = await axios.get(API + '/capacitaciones/' + capacitacion.id + '/videos');
      var archivosRes = await axios.get(API + '/capacitaciones/' + capacitacion.id + '/archivos');
      setVideos(videosRes.data);
      setArchivos(archivosRes.data);
    } catch (e) {
      setVideos([]);
      setArchivos([]);
    }
    
    setIsDialogOpen(true);
  }

  async function openConfigDialog(capacitacion) {
    setSelectedCapForConfig(capacitacion);
    setIsConfigDialogOpen(true);
    setCargoSearch('');
    try {
      var [pregRes, preqRes, ccRes, acRes] = await Promise.all([
        axios.get(API + '/capacitaciones/' + capacitacion.id + '/preguntas'),
        axios.get(API + '/capacitaciones/' + capacitacion.id + '/prerequisitos'),
        axios.get(API + '/cargo-capacitaciones'),
        axios.get(API + '/area-capacitaciones'),
      ]);
      setPreguntas(pregRes.data);
      setPrerequisitos(preqRes.data);
      setAsignCargos(ccRes.data.filter(r => r.capacitacion_id === capacitacion.id));
      setAsignAreas(acRes.data.filter(r => r.capacitacion_id === capacitacion.id));
    } catch (error) {
      toast.error('Error al cargar configuracion');
    }
  }

  async function handleAddCargoAsign(cargoId) {
    if (!selectedCapForConfig) return;
    try {
      await axios.post(API + '/cargo-capacitaciones', { cargo_id: cargoId, capacitacion_id: selectedCapForConfig.id });
      var res = await axios.get(API + '/cargo-capacitaciones');
      setAsignCargos(res.data.filter(r => r.capacitacion_id === selectedCapForConfig.id));
      toast.success('Cargo asignado');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  }

  async function handleRemoveCargoAsign(ccId) {
    try {
      await axios.delete(API + '/cargo-capacitaciones/' + ccId);
      setAsignCargos(prev => prev.filter(r => r.id !== ccId));
      toast.success('Cargo desasignado');
    } catch { toast.error('Error al eliminar'); }
  }

  async function handleAddAreaAsign(areaId) {
    if (!selectedCapForConfig) return;
    try {
      await axios.post(API + '/area-capacitaciones', { area_id: areaId, capacitacion_id: selectedCapForConfig.id });
      var res = await axios.get(API + '/area-capacitaciones');
      setAsignAreas(res.data.filter(r => r.capacitacion_id === selectedCapForConfig.id));
      toast.success('Area asignada');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  }

  async function handleRemoveAreaAsign(acId) {
    try {
      await axios.delete(API + '/area-capacitaciones/' + acId);
      setAsignAreas(prev => prev.filter(r => r.id !== acId));
      toast.success('Area desasignada');
    } catch { toast.error('Error al eliminar'); }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      var payload = {
        nombre: formData.nombre,
        descripcion: formData.descripcion,
        vigencia_meses: formData.vigencia_indefinida ? null : formData.vigencia_meses,
        obligatoria: formData.obligatoria,
        es_transversal: formData.es_transversal,
        area_id: formData.es_transversal ? null : (formData.area_id || null),
        actividad_id: formData.es_transversal ? null : (formData.actividad_id || null),
        video_url: formData.video_url || null,
        pdf_url: formData.pdf_url || null,
        horas: formData.horas ? parseFloat(formData.horas) : null,
        modalidad: formData.modalidad,
        porcentaje_aprobacion: formData.porcentaje_aprobacion,
        imagen_url: formData.imagen_url || null,
        tiempo_evaluacion_minutos: formData.tiempo_ilimitado ? null : formData.tiempo_evaluacion_minutos,
        intentos_maximos: formData.intentos_ilimitados ? 999 : formData.intentos_maximos,
        calendarizable: formData.calendarizable,
        categoria: formData.categoria || null
      };
      
      if (editingCapacitacion) {
        await axios.put(API + '/capacitaciones/' + editingCapacitacion.id, payload);
        toast.success('Capacitación actualizada');
      } else {
        await axios.post(API + '/capacitaciones', payload);
        toast.success('Capacitación creada');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  }

  async function handleDelete(id) {
    setDeleteDialog({ open: true, itemId: id, tipo: 'capacitacion', deps: [], loadingDeps: true });
    try {
      var res = await axios.get(`${API}/dependencias/capacitaciones/${id}`);
      setDeleteDialog(prev => ({ ...prev, deps: res.data.dependencias, loadingDeps: false }));
    } catch { setDeleteDialog(prev => ({ ...prev, loadingDeps: false })); }
  }

  async function handleAddPregunta() {
    if (!selectedCapForConfig) return;
    try {
      var payload = {
        capacitacion_id: selectedCapForConfig.id,
        pregunta: preguntaForm.pregunta,
        alternativa_a: preguntaForm.alternativa_a,
        alternativa_b: preguntaForm.alternativa_b,
        alternativa_c: preguntaForm.alternativa_c,
        alternativa_d: preguntaForm.alternativa_d,
        respuesta_correcta: preguntaForm.respuesta_correcta,
        orden: preguntas.length + 1
      };
      await axios.post(API + '/capacitaciones/' + selectedCapForConfig.id + '/preguntas', payload);
      toast.success('Pregunta agregada');
      setPreguntaForm({
        pregunta: '', alternativa_a: '', alternativa_b: '', alternativa_c: '', alternativa_d: '',
        respuesta_correcta: 'A', orden: 1
      });
      var pregRes = await axios.get(API + '/capacitaciones/' + selectedCapForConfig.id + '/preguntas');
      setPreguntas(pregRes.data);
    } catch (error) {
      toast.error('Error al agregar pregunta');
    }
  }

  async function handleDeletePregunta(preguntaId) {
    setDeleteDialog({ open: true, itemId: preguntaId, tipo: 'pregunta', deps: [], loadingDeps: false });
  }

  async function confirmDeleteItem() {
    var { itemId, tipo } = deleteDialog;
    try {
      if (tipo === 'capacitacion') {
        await axios.delete(API + '/capacitaciones/' + itemId);
        toast.success('Capacitacion eliminada');
        setDeleteDialog({ open: false, itemId: null, tipo: '', deps: [], loadingDeps: false });
        fetchData();
      } else if (tipo === 'pregunta') {
        await axios.delete(API + '/capacitaciones/' + selectedCapForConfig.id + '/preguntas/' + itemId);
        toast.success('Pregunta eliminada');
        setDeleteDialog({ open: false, itemId: null, tipo: '', deps: [], loadingDeps: false });
        var pregRes = await axios.get(API + '/capacitaciones/' + selectedCapForConfig.id + '/preguntas');
        setPreguntas(pregRes.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
      setDeleteDialog({ open: false, itemId: null, tipo: '', deps: [], loadingDeps: false });
    }
  }

  async function handleAddPrerequisito(prereqId) {
    if (!selectedCapForConfig || !prereqId) return;
    try {
      await axios.post(API + '/capacitaciones/' + selectedCapForConfig.id + '/prerequisitos', {
        capacitacion_id: selectedCapForConfig.id,
        prerequisito_id: prereqId
      });
      toast.success('Prerrequisito agregado');
      var preqRes = await axios.get(API + '/capacitaciones/' + selectedCapForConfig.id + '/prerequisitos');
      setPrerequisitos(preqRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar');
    }
  }

  async function handleDeletePrerequisito(prereqId) {
    try {
      await axios.delete(API + '/capacitaciones/' + selectedCapForConfig.id + '/prerequisitos/' + prereqId);
      toast.success('Prerrequisito eliminado');
      var preqRes = await axios.get(API + '/capacitaciones/' + selectedCapForConfig.id + '/prerequisitos');
      setPrerequisitos(preqRes.data);
    } catch (error) {
      toast.error('Error al eliminar');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  var filteredCapacitaciones = getFilteredCapacitaciones();
  var availablePrerequisitos = capacitaciones.filter(function(c) {
    if (!selectedCapForConfig) return false;
    if (c.id === selectedCapForConfig.id) return false;
    for (var i = 0; i < prerequisitos.length; i++) {
      if (prerequisitos[i].prerequisito_id === c.id) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Capacitaciones</h1>
          <p className="text-slate-500">Gestión de cursos y programas de capacitación</p>
        </div>
        {isAdmin() && (
          <Button 
            onClick={openCreateDialog}
            className="bg-teal-500 hover:bg-teal-600"
            data-testid="create-capacitacion-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Curso
          </Button>
        )}
      </div>

      <Card className="border border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar capacitación..."
                value={search}
                onChange={function(e) { setSearch(e.target.value); }}
                className="pl-9"
                data-testid="search-capacitaciones"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Curso</TableHead>
                <TableHead>Aplica a</TableHead>
                <TableHead>Modalidad</TableHead>
                <TableHead>Vigencia</TableHead>
                <TableHead>Horas</TableHead>
                {isAdmin() && <TableHead className="text-right">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCapacitaciones.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                    No hay capacitaciones registradas
                  </TableCell>
                </TableRow>
              ) : (
                filteredCapacitaciones.map(function(cap) {
                  return (
                    <TableRow key={cap.id} data-testid={'capacitacion-row-' + cap.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                            <GraduationCap className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-medium">{cap.nombre}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              {cap.obligatoria && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-600 rounded font-medium">Obligatoria</span>
                              )}
                              {cap.calendarizable && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-teal-100 text-teal-600 rounded font-medium">Calendarizable</span>
                              )}
                              {cap.video_url && <Video className="w-3.5 h-3.5 text-blue-500" title="Tiene video" />}
                              {cap.pdf_url && <FileText className="w-3.5 h-3.5 text-red-500" title="Tiene PDF" />}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {cap.es_transversal ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                              <Globe className="w-3 h-3" />
                              Transversal
                            </span>
                          ) : (
                            <>
                              {cap.area_id && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                                  <Building2 className="w-3 h-3" />
                                  {getAreaName(cap.area_id)}
                                </span>
                              )}
                              {cap.actividad_id && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                                  <HardHat className="w-3 h-3" />
                                  {cap.actividad_nombre || getActividadName(cap.actividad_id)}
                                </span>
                              )}
                              {!cap.area_id && !cap.actividad_id && (
                                <span className="text-xs text-slate-400">Sin asignación</span>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={'px-2 py-1 text-xs font-medium rounded-full ' + 
                          (cap.modalidad === 'e-learning' ? 'bg-cyan-100 text-cyan-700' : 
                           cap.modalidad === 'presencial' ? 'bg-amber-100 text-amber-700' : 'bg-violet-100 text-violet-700')}>
                          {cap.modalidad === 'e-learning' ? 'E-Learning' : cap.modalidad === 'presencial' ? 'Presencial' : 'Mixta'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {cap.vigencia_meses === null ? (
                          <div className="flex items-center gap-1 text-emerald-600 text-sm">
                            <Infinity className="w-4 h-4" />
                            Indefinida
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-slate-600 text-sm">
                            <Clock className="w-4 h-4" />
                            {cap.vigencia_meses}m
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {cap.horas ? cap.horas + 'h' : '-'}
                      </TableCell>
                      {isAdmin() && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={function() { openConfigDialog(cap); }}
                              title="Configurar preguntas y prerrequisitos"
                              data-testid={'config-capacitacion-' + cap.id}
                            >
                              <Settings className="w-4 h-4 text-slate-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={function() { openEditDialog(cap); }}
                              data-testid={'edit-capacitacion-' + cap.id}
                            >
                              <Pencil className="w-4 h-4 text-slate-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={function() { handleDelete(cap.id); }}
                              data-testid={'delete-capacitacion-' + cap.id}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCapacitacion ? 'Editar Curso' : 'Nuevo Curso'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Nombre del Curso *</Label>
                <Input
                  value={formData.nombre}
                  onChange={function(e) { setFormData({ ...formData, nombre: e.target.value }); }}
                  required
                  data-testid="capacitacion-nombre-input"
                />
              </div>
              
              <div className="col-span-2 space-y-2">
                <Label>Descripción</Label>
                <Textarea
                  value={formData.descripcion}
                  onChange={function(e) { setFormData({ ...formData, descripcion: e.target.value }); }}
                  rows={2}
                />
              </div>

              {/* Switch Transversal */}
              <div className="col-span-2 flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div>
                  <Label className="font-medium text-purple-900">¿Es Transversal?</Label>
                  <p className="text-xs text-purple-600">Aplica a todas las áreas y actividades</p>
                </div>
                <Switch
                  checked={formData.es_transversal}
                  onCheckedChange={function(c) { setFormData({ ...formData, es_transversal: c }); }}
                />
              </div>

              <div className="space-y-2">
                <Label className={formData.es_transversal ? 'text-slate-400' : ''}>
                  Aplica a Área <span className="text-xs text-slate-400">(opcional)</span>
                </Label>
                <Select
                  value={formData.area_id || 'none'}
                  onValueChange={function(v) { setFormData({ ...formData, area_id: v === 'none' ? '' : v }); }}
                  disabled={formData.es_transversal}
                >
                  <SelectTrigger className={formData.es_transversal ? 'opacity-50' : ''}>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin área específica</SelectItem>
                    {areas.map(function(a) {
                      return <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className={formData.es_transversal ? 'text-slate-400' : ''}>
                  Aplica a Actividad <span className="text-xs text-slate-400">(opcional)</span>
                </Label>
                <Select
                  value={formData.actividad_id || 'none'}
                  onValueChange={function(v) { setFormData({ ...formData, actividad_id: v === 'none' ? '' : v }); }}
                  disabled={formData.es_transversal}
                >
                  <SelectTrigger className={formData.es_transversal ? 'opacity-50' : ''}>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin actividad específica</SelectItem>
                    {cargos.map(function(c) {
                      return <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Modalidad</Label>
                <Select
                  value={formData.modalidad}
                  onValueChange={function(v) { setFormData({ ...formData, modalidad: v }); }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODALIDADES.map(function(m) {
                      return <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select
                  value={formData.categoria || '_none'}
                  onValueChange={function(v) { setFormData({ ...formData, categoria: v === '_none' ? '' : v }); }}
                >
                  <SelectTrigger data-testid="select-categoria">
                    <SelectValue placeholder="Sin categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Sin categoria</SelectItem>
                    {CATEGORIAS.map(function(c) {
                      return <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2 border-t pt-4 mt-2">
                <h4 className="font-medium text-sm text-slate-700 mb-3 flex items-center gap-2">
                  <Video className="w-4 h-4" /> Contenido del Curso
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>URL de Imagen de Portada</Label>
                    <Input
                      value={formData.imagen_url}
                      onChange={function(e) { setFormData({ ...formData, imagen_url: e.target.value }); }}
                      placeholder="https://images.unsplash.com/..."
                    />
                    <p className="text-xs text-slate-500">Imagen que se mostrará en la tarjeta del curso</p>
                  </div>
                  <div className="space-y-2">
                    <Label>URL del Video Principal (YouTube/Vimeo)</Label>
                    <Input
                      value={formData.video_url}
                      onChange={function(e) { setFormData({ ...formData, video_url: e.target.value }); }}
                      placeholder="https://youtube.com/embed/..."
                    />
                  </div>
                </div>
                
                {/* Archivos múltiples */}
                <div className="mt-4 space-y-2">
                  <Label className="flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Archivos de Apoyo (PDFs)
                  </Label>
                  <div className="space-y-2">
                    {archivos.map(function(archivo, idx) {
                      return (
                        <div key={archivo.id || idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                          <FileText className="w-4 h-4 text-red-500" />
                          <span className="flex-1 text-sm truncate">{archivo.titulo}</span>
                          <a href={archivo.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">Ver</a>
                          {editingCapacitacion && (
                            <button
                              type="button"
                              onClick={async function() {
                                try {
                                  await axios.delete(API + '/capacitaciones/' + editingCapacitacion.id + '/archivos/' + archivo.id);
                                  setArchivos(archivos.filter(function(a) { return a.id !== archivo.id; }));
                                  toast.success('Archivo eliminado');
                                } catch (e) {
                                  toast.error('Error al eliminar');
                                }
                              }}
                              className="text-red-500 hover:text-red-700 text-xs"
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Agregar nuevo archivo */}
                  {editingCapacitacion && (
                    <div className="flex gap-2 mt-2">
                      <Input
                        id="nuevo-archivo-titulo"
                        placeholder="Título del archivo"
                        className="flex-1"
                      />
                      <Input
                        id="nuevo-archivo-url"
                        placeholder="URL del PDF"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={async function() {
                          var titulo = document.getElementById('nuevo-archivo-titulo').value;
                          var url = document.getElementById('nuevo-archivo-url').value;
                          if (!titulo || !url) {
                            toast.error('Completa título y URL');
                            return;
                          }
                          try {
                            var res = await axios.post(API + '/capacitaciones/' + editingCapacitacion.id + '/archivos', {
                              capacitacion_id: editingCapacitacion.id,
                              titulo: titulo,
                              url: url,
                              tipo: 'pdf',
                              orden: archivos.length + 1
                            });
                            setArchivos([...archivos, res.data]);
                            document.getElementById('nuevo-archivo-titulo').value = '';
                            document.getElementById('nuevo-archivo-url').value = '';
                            toast.success('Archivo agregado');
                          } catch (e) {
                            toast.error('Error al agregar');
                          }
                        }}
                      >
                        + Agregar
                      </Button>
                    </div>
                  )}
                  {!editingCapacitacion && (
                    <p className="text-xs text-slate-400">Guarda el curso primero para agregar archivos adicionales</p>
                  )}
                </div>
              </div>

              <div className="col-span-2 border-t pt-4 mt-2">
                <h4 className="font-medium text-sm text-slate-700 mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Vigencia y Evaluación
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Horas de Capacitación</Label>
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      value={formData.horas}
                      onChange={function(e) { setFormData({ ...formData, horas: e.target.value }); }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Vigencia (meses)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="1"
                        value={formData.vigencia_meses}
                        onChange={function(e) { setFormData({ ...formData, vigencia_meses: parseInt(e.target.value) || 12 }); }}
                        disabled={formData.vigencia_indefinida}
                        className={formData.vigencia_indefinida ? 'opacity-50' : ''}
                      />
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <Switch
                        checked={formData.vigencia_indefinida}
                        onCheckedChange={function(c) { setFormData({ ...formData, vigencia_indefinida: c }); }}
                      />
                      <span className="text-xs text-slate-500">Indefinida</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>% Aprobación</Label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={formData.porcentaje_aprobacion}
                      onChange={function(e) { setFormData({ ...formData, porcentaje_aprobacion: parseInt(e.target.value) || 70 }); }}
                    />
                  </div>
                </div>
                
                {/* Configuración de evaluación */}
                <div className="grid grid-cols-2 gap-4 mt-4 p-3 bg-slate-50 rounded-lg">
                  <div className="space-y-2">
                    <Label>Tiempo para Evaluación (minutos)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="1"
                        value={formData.tiempo_evaluacion_minutos}
                        onChange={function(e) { setFormData({ ...formData, tiempo_evaluacion_minutos: parseInt(e.target.value) || 30 }); }}
                        disabled={formData.tiempo_ilimitado}
                        className={formData.tiempo_ilimitado ? 'opacity-50' : ''}
                      />
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <Switch
                        checked={formData.tiempo_ilimitado}
                        onCheckedChange={function(c) { setFormData({ ...formData, tiempo_ilimitado: c }); }}
                      />
                      <span className="text-xs text-slate-500">Sin límite de tiempo</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Intentos Máximos</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="1"
                        value={formData.intentos_maximos}
                        onChange={function(e) { setFormData({ ...formData, intentos_maximos: parseInt(e.target.value) || 3 }); }}
                        disabled={formData.intentos_ilimitados}
                        className={formData.intentos_ilimitados ? 'opacity-50' : ''}
                      />
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <Switch
                        checked={formData.intentos_ilimitados}
                        onCheckedChange={function(c) { setFormData({ ...formData, intentos_ilimitados: c }); }}
                      />
                      <span className="text-xs text-slate-500">Intentos ilimitados</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-span-2 flex items-center justify-between border-t pt-4">
                <Label className="font-medium">¿Es obligatoria?</Label>
                <Switch
                  checked={formData.obligatoria}
                  onCheckedChange={function(c) { setFormData({ ...formData, obligatoria: c }); }}
                />
              </div>
              <div className="col-span-2 flex items-center justify-between">
                <div>
                  <Label className="font-medium">¿Calendarizable?</Label>
                  <p className="text-xs text-slate-400 mt-0.5">Se puede agendar en fechas especificas en el calendario</p>
                </div>
                <Switch
                  checked={formData.calendarizable}
                  onCheckedChange={function(c) { setFormData({ ...formData, calendarizable: c }); }}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={function() { setIsDialogOpen(false); }}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-teal-500 hover:bg-teal-600">
                {editingCapacitacion ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Config Dialog - Preguntas y Prerequisitos */}
      <Dialog open={isConfigDialogOpen} onOpenChange={setIsConfigDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configurar: {selectedCapForConfig?.nombre}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="preguntas" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="preguntas" className="flex items-center gap-1 text-xs">
                <HelpCircle className="w-3.5 h-3.5" />
                Preguntas ({preguntas.length})
              </TabsTrigger>
              <TabsTrigger value="prerequisitos" className="flex items-center gap-1 text-xs">
                <Link2 className="w-3.5 h-3.5" />
                Prerrequisitos ({prerequisitos.length})
              </TabsTrigger>
              <TabsTrigger value="cargos" className="flex items-center gap-1 text-xs">
                <Wrench className="w-3.5 h-3.5" />
                Por Cargo ({asignCargos.length})
              </TabsTrigger>
              <TabsTrigger value="areas" className="flex items-center gap-1 text-xs">
                <MapPin className="w-3.5 h-3.5" />
                Por Area ({asignAreas.length})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="preguntas" className="space-y-4 mt-4">
              {/* Add Pregunta Form */}
              <Card className="border-dashed">
                <CardContent className="pt-4 space-y-3">
                  <div className="space-y-2">
                    <Label>Pregunta</Label>
                    <Textarea
                      value={preguntaForm.pregunta}
                      onChange={function(e) { setPreguntaForm({ ...preguntaForm, pregunta: e.target.value }); }}
                      placeholder="Escriba la pregunta..."
                      rows={2}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">A)</Label>
                      <Input
                        value={preguntaForm.alternativa_a}
                        onChange={function(e) { setPreguntaForm({ ...preguntaForm, alternativa_a: e.target.value }); }}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">B)</Label>
                      <Input
                        value={preguntaForm.alternativa_b}
                        onChange={function(e) { setPreguntaForm({ ...preguntaForm, alternativa_b: e.target.value }); }}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">C)</Label>
                      <Input
                        value={preguntaForm.alternativa_c}
                        onChange={function(e) { setPreguntaForm({ ...preguntaForm, alternativa_c: e.target.value }); }}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">D)</Label>
                      <Input
                        value={preguntaForm.alternativa_d}
                        onChange={function(e) { setPreguntaForm({ ...preguntaForm, alternativa_d: e.target.value }); }}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs">Respuesta Correcta</Label>
                      <Select
                        value={preguntaForm.respuesta_correcta}
                        onValueChange={function(v) { setPreguntaForm({ ...preguntaForm, respuesta_correcta: v }); }}
                      >
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="A">A</SelectItem>
                          <SelectItem value="B">B</SelectItem>
                          <SelectItem value="C">C</SelectItem>
                          <SelectItem value="D">D</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      type="button" 
                      onClick={handleAddPregunta}
                      disabled={!preguntaForm.pregunta || !preguntaForm.alternativa_a}
                      className="mt-5 bg-teal-500 hover:bg-teal-600"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Agregar Pregunta
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* List Preguntas */}
              <div className="space-y-2">
                {preguntas.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No hay preguntas configuradas</p>
                ) : (
                  preguntas.map(function(p, idx) {
                    return (
                      <Card key={p.id} className="bg-slate-50">
                        <CardContent className="py-3 px-4">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <p className="font-medium text-sm">{idx + 1}. {p.pregunta}</p>
                              <div className="grid grid-cols-2 gap-1 mt-2 text-xs text-slate-600">
                                <span className={p.respuesta_correcta === 'A' ? 'text-green-600 font-medium' : ''}>A) {p.alternativa_a}</span>
                                <span className={p.respuesta_correcta === 'B' ? 'text-green-600 font-medium' : ''}>B) {p.alternativa_b}</span>
                                <span className={p.respuesta_correcta === 'C' ? 'text-green-600 font-medium' : ''}>C) {p.alternativa_c}</span>
                                <span className={p.respuesta_correcta === 'D' ? 'text-green-600 font-medium' : ''}>D) {p.alternativa_d}</span>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={function() { handleDeletePregunta(p.id); }}
                              className="text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="prerequisitos" className="space-y-4 mt-4">
              {/* Add Prerequisito */}
              <div className="flex items-end gap-2">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Agregar Prerrequisito</Label>
                  <Select onValueChange={handleAddPrerequisito}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar curso prerrequisito" />
                    </SelectTrigger>
                    <SelectContent>
                      {availablePrerequisitos.map(function(c) {
                        return <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* List Prerequisitos */}
              <div className="space-y-2 mt-4">
                {prerequisitos.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No hay prerrequisitos configurados</p>
                ) : (
                  prerequisitos.map(function(p) {
                    return (
                      <div key={p.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                          <span className="text-sm font-medium">{p.prerequisito_nombre}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={function() { handleDeletePrerequisito(p.id); }}
                          className="text-red-500 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })
                )}
              </div>
              
              <p className="text-xs text-slate-500 mt-2">
                Los prerrequisitos son cursos que el trabajador debe aprobar antes de poder acceder a este curso.
              </p>
            </TabsContent>
            
            <TabsContent value="cargos" className="space-y-4 mt-4">
              <p className="text-xs text-slate-500">Asigne los cargos que requieren esta capacitacion. Ej: Electrico, Mecanico, Soldador, Rigger.</p>
              <div className="space-y-2">
                <Input
                  placeholder="Buscar cargo..."
                  value={cargoSearch}
                  onChange={(e) => setCargoSearch(e.target.value)}
                  className="h-8 text-sm"
                  data-testid="cargo-search"
                />
                <div className="max-h-48 overflow-y-auto border rounded-lg">
                  {cargosReales
                    .filter(c => !asignCargos.some(ac => ac.cargo_id === c.id))
                    .filter(c => !cargoSearch || c.nombre.toLowerCase().includes(cargoSearch.toLowerCase()))
                    .slice(0, 20)
                    .map(c => (
                      <button key={c.id} onClick={() => handleAddCargoAsign(c.id)}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-teal-50 border-b last:border-0 flex items-center justify-between"
                        data-testid={`add-cargo-${c.id}`}>
                        <span>{c.nombre}</span>
                        <Plus className="w-3.5 h-3.5 text-teal-500" />
                      </button>
                    ))}
                  {cargosReales.filter(c => !asignCargos.some(ac => ac.cargo_id === c.id)).filter(c => !cargoSearch || c.nombre.toLowerCase().includes(cargoSearch.toLowerCase())).length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-3">No hay cargos disponibles</p>
                  )}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Cargos asignados ({asignCargos.length})</Label>
                {asignCargos.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-3">Sin cargos asignados</p>
                ) : (
                  asignCargos.map(ac => (
                    <div key={ac.id} className="flex items-center justify-between px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                      <span className="text-sm font-medium text-blue-800 flex items-center gap-2">
                        <Wrench className="w-3.5 h-3.5" /> {ac.cargo_nombre}
                      </span>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500 hover:text-red-600" onClick={() => handleRemoveCargoAsign(ac.id)}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>

            <TabsContent value="areas" className="space-y-4 mt-4">
              <p className="text-xs text-slate-500">Asigne las areas que requieren esta capacitacion.</p>
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  {areas.filter(a => !asignAreas.some(aa => aa.area_id === a.id)).map(a => (
                    <button key={a.id} onClick={() => handleAddAreaAsign(a.id)}
                      className="text-left px-3 py-2 text-sm border rounded-lg hover:bg-emerald-50 hover:border-emerald-300 flex items-center justify-between"
                      data-testid={`add-area-${a.id}`}>
                      <span className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-slate-400" /> {a.nombre}</span>
                      <Plus className="w-3.5 h-3.5 text-emerald-500" />
                    </button>
                  ))}
                </div>
                {areas.filter(a => !asignAreas.some(aa => aa.area_id === a.id)).length === 0 && (
                  <p className="text-xs text-slate-400 text-center py-3">Todas las areas ya estan asignadas</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Areas asignadas ({asignAreas.length})</Label>
                {asignAreas.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-3">Sin areas asignadas</p>
                ) : (
                  asignAreas.map(aa => (
                    <div key={aa.id} className="flex items-center justify-between px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <span className="text-sm font-medium text-emerald-800 flex items-center gap-2">
                        <MapPin className="w-3.5 h-3.5" /> {aa.area_nombre}
                      </span>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500 hover:text-red-600" onClick={() => handleRemoveAreaAsign(aa.id)}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={function() { setIsConfigDialogOpen(false); }}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={deleteDialog.open}
        onOpenChange={(v) => !v && setDeleteDialog({ open: false, itemId: null, tipo: '', deps: [], loadingDeps: false })}
        onConfirm={confirmDeleteItem}
        title={deleteDialog.tipo === 'capacitacion' ? 'Eliminar Capacitacion' : 'Eliminar Pregunta'}
        description={deleteDialog.tipo === 'capacitacion' 
          ? '¿Esta seguro de eliminar esta capacitacion? Esta accion no se puede deshacer.'
          : '¿Esta seguro de eliminar esta pregunta?'}
        dependencies={deleteDialog.deps}
        loadingDeps={deleteDialog.loadingDeps}
      />
    </div>
  );
};